import { AGE_BANDS, CAPABILITY_LEVELS } from './enums.js';
import { PROCEDURES, procedureByKey, searchProcedures } from './procedures.js';
/**
 * Importing a logbook from somewhere else.
 *
 * The rules are all here, as pure functions, because this is the part that
 * decides what a trainee's three years of records turn into. Matching is
 * **deterministic string comparison only** — no AI, no network, no service. It
 * has to work on a train, it has to give the same answer twice, and a trainee
 * has to be able to see why a row was matched the way it was and correct it.
 *
 * Nothing here writes anything. Every step produces a plan the user confirms.
 */
/* ------------------------------------------------------------- normalising */
export function normalise(value) {
    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/* ---------------------------------------------------------- column mapping */
/** The fields an imported row can populate. Everything else is ignored. */
export const IMPORT_FIELDS = [
    'procedure',
    'date',
    'time',
    'capabilityLevel',
    'ageBand',
    'variant',
    'side',
    'ultrasoundGuided',
    'attempts',
    'successful',
    'complications',
    'indication',
    'note',
];
export const IMPORT_FIELD_LABELS = {
    procedure: 'Procedure',
    date: 'Date',
    time: 'Time',
    capabilityLevel: 'Capability level',
    ageBand: 'Age band',
    variant: 'Variant or site',
    side: 'Side',
    ultrasoundGuided: 'Ultrasound guided',
    attempts: 'Attempts',
    successful: 'Successful',
    complications: 'Complications',
    indication: 'Indication',
    note: 'Note',
};
/**
 * What other logbooks and spreadsheets tend to call these things.
 *
 * "Supervision" maps to capability level deliberately: it is what most existing
 * logbooks call the FICM scale, even though this app never does. See CLAUDE.md
 * on vocabulary — we do not adopt the word, but we do have to recognise it.
 */
const HEADER_HINTS = {
    procedure: ['procedure', 'procedure name', 'proc', 'skill', 'intervention', 'activity', 'what'],
    date: ['date', 'date performed', 'performed on', 'day', 'when', 'date time', 'datetime'],
    time: ['time', 'time performed', 'start time', 'clock'],
    capabilityLevel: [
        'capability level',
        'capability',
        'level',
        'supervision',
        'supervision level',
        'grade',
        'independence',
        'competence',
    ],
    ageBand: ['age band', 'age', 'age group', 'patient age', 'age range'],
    variant: ['variant', 'site', 'approach', 'technique', 'route', 'position'],
    side: ['side', 'laterality', 'left right'],
    ultrasoundGuided: ['ultrasound', 'ultrasound guided', 'us guided', 'us', 'usg', 'image guided'],
    attempts: ['attempts', 'number of attempts', 'passes', 'tries'],
    successful: ['successful', 'success', 'outcome', 'completed'],
    complications: ['complications', 'complication', 'problems', 'adverse'],
    indication: ['indication', 'reason', 'why', 'indication for procedure'],
    note: ['note', 'notes', 'comment', 'comments', 'reflection', 'free text'],
};
/**
 * Guesses which column is which, exactly once, as a starting point the user
 * corrects. An exact match beats a prefix; a prefix beats a substring. Nothing
 * is guessed twice — the first field to claim a column keeps it.
 */
export function guessColumnMapping(headers) {
    const mapping = {};
    const taken = new Set();
    const score = (header, hints) => {
        const value = normalise(header);
        if (value === '')
            return -1;
        for (const hint of hints) {
            if (value === hint)
                return 3;
        }
        for (const hint of hints) {
            if (value.startsWith(hint) || hint.startsWith(value))
                return 2;
        }
        for (const hint of hints) {
            if (value.includes(hint))
                return 1;
        }
        return -1;
    };
    for (const field of IMPORT_FIELDS) {
        let best = null;
        for (const header of headers) {
            if (taken.has(header))
                continue;
            const value = score(header, HEADER_HINTS[field]);
            if (value > 0 && (best === null || value > best.score))
                best = { header, score: value };
        }
        if (best !== null) {
            mapping[field] = best.header;
            taken.add(best.header);
        }
    }
    return mapping;
}
/* ------------------------------------------------------------------ dates */
export const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
/**
 * Long-form English dates — `9 February 2023`, which is what the Anaesthetics
 * Lifelong Learning Platform exports.
 *
 * Unambiguous by construction: a named month cannot be mistaken for a day, so
 * these are read whatever `DateFormat` the wizard settled on. Ordinal suffixes
 * and three-letter abbreviations are tolerated because people hand-edit these
 * files. See `docs/IMPORT_SOURCES.md`.
 */
const MONTH_NAMES = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];
/**
 * Excel stores a date as a number: `42736` is the 1st of January 2017, counted
 * in days from the 30th of December 1899.
 *
 * Recognised here rather than in the workbook reader, which would otherwise
 * have to read style records to work out which numbers are dates. The range is
 * bounded to roughly 1954–2064 so an ordinary number in a date column — an age,
 * a count — is rejected rather than becoming a date in the 1900s.
 *
 * The fractional part is the time of day, and is used where the sheet has one.
 */
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const EXCEL_SERIAL_MIN = 20_000;
const EXCEL_SERIAL_MAX = 60_000;
export function parseExcelSerial(value) {
    const text = value.trim();
    if (!/^\d+(\.\d+)?$/.test(text))
        return null;
    const serial = Number(text);
    if (serial < EXCEL_SERIAL_MIN || serial > EXCEL_SERIAL_MAX)
        return null;
    const days = Math.floor(serial);
    const at = new Date(EXCEL_EPOCH + days * 86_400_000);
    const dayFraction = serial - days;
    const totalMinutes = Math.round(dayFraction * 24 * 60);
    return {
        year: at.getUTCFullYear(),
        month: at.getUTCMonth() + 1,
        day: at.getUTCDate(),
        hours: Math.floor(totalMinutes / 60) % 24,
        minutes: totalMinutes % 60,
    };
}
/** `9 February 2023`, `9th Feb 2023`, `February 9 2023`. Null if it is not one. */
export function parseLongFormDate(value) {
    const text = value.trim().toLowerCase().replace(/,/g, ' ');
    // Day first, then month name: "9 February 2023".
    const dayFirst = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?\s+(\d{4})/.exec(text);
    // Month name first: "February 9 2023".
    const monthFirst = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?\s+(\d{4})/.exec(text);
    const found = dayFirst !== null
        ? { day: Number(dayFirst[1]), name: dayFirst[2] ?? '', year: Number(dayFirst[3]) }
        : monthFirst !== null
            ? { day: Number(monthFirst[2]), name: monthFirst[1] ?? '', year: Number(monthFirst[3]) }
            : null;
    if (found === null)
        return null;
    const index = MONTH_NAMES.findIndex((month) => month === found.name || month.slice(0, 3) === found.name.slice(0, 3));
    if (index === -1)
        return null;
    return { year: found.year, month: index + 1, day: found.day };
}
/**
 * Works out which way round the day and month are.
 *
 * Defaults to DD/MM/YYYY, because these are UK trainees and getting this
 * backwards silently moves half a logbook by up to eleven months. Where a
 * column contains any value with a first part above 12, the answer is certain,
 * and the wizard shows what it decided and lets the user override it.
 */
export function detectDateFormat(samples) {
    let dayFirstCertain = false;
    let monthFirstCertain = false;
    for (const sample of samples) {
        const text = sample.trim();
        if (text === '')
            continue;
        if (/^\d{4}-\d{1,2}-\d{1,2}/.test(text))
            return 'YYYY-MM-DD';
        // A named month carries its own order, so it votes for nothing.
        if (parseLongFormDate(text) !== null)
            continue;
        const parts = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(text);
        if (parts === null)
            continue;
        const first = Number(parts[1]);
        const second = Number(parts[2]);
        if (first > 12)
            dayFirstCertain = true;
        if (second > 12)
            monthFirstCertain = true;
    }
    if (monthFirstCertain && !dayFirstCertain)
        return 'MM/DD/YYYY';
    return 'DD/MM/YYYY';
}
/**
 * Parses one cell into a local date, optionally with a time from another cell.
 * Returns null rather than a guess — a row with an unreadable date is reported
 * and skipped, never imported at some default moment.
 */
export function parseImportedDate(value, format, time) {
    const text = value.trim();
    if (text === '')
        return null;
    let year;
    let month;
    let day;
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
    const parts = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(text);
    const longForm = parseLongFormDate(text);
    const serial = parseExcelSerial(text);
    if (serial !== null) {
        year = serial.year;
        month = serial.month;
        day = serial.day;
        // A serial carries its own time of day. Only used where the sheet has no
        // separate time column, which the caller signals by passing none.
        if (time === undefined && (serial.hours !== 0 || serial.minutes !== 0)) {
            time = `${String(serial.hours)}:${String(serial.minutes).padStart(2, '0')}`;
        }
    }
    else if (longForm !== null) {
        year = longForm.year;
        month = longForm.month;
        day = longForm.day;
    }
    else if (iso !== null) {
        year = Number(iso[1]);
        month = Number(iso[2]);
        day = Number(iso[3]);
    }
    else if (parts !== null) {
        const first = Number(parts[1]);
        const second = Number(parts[2]);
        year = Number(parts[3]);
        if (format === 'MM/DD/YYYY') {
            month = first;
            day = second;
        }
        else {
            day = first;
            month = second;
        }
        // A two-digit year is this century: a logbook is not from 1998.
        if (year < 100)
            year += 2000;
    }
    else {
        return null;
    }
    if (year === undefined || month === undefined || day === undefined)
        return null;
    if (month < 1 || month > 12 || day < 1 || day > 31)
        return null;
    let hours = 0;
    let minutes = 0;
    if (time !== undefined && time.trim() !== '') {
        // A band first — `night-2200-0800` has digits in it and would otherwise be
        // read as 22:00 by the clock regex below, which is a different claim.
        const band = parseTimeBand(time);
        if (band !== null) {
            hours = band.hours;
            minutes = band.minutes;
        }
        else {
            const clock = /^(\d{1,2})[:.h]?(\d{2})?/.exec(time.trim());
            if (clock !== null) {
                hours = Number(clock[1]);
                minutes = Number(clock[2] ?? 0);
                if (hours > 23 || minutes > 59) {
                    hours = 0;
                    minutes = 0;
                }
            }
        }
    }
    const date = new Date(year, month - 1, day, hours, minutes);
    // Rolls over silently otherwise: 31 February would become 3 March.
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
}
/* --------------------------------------------------- supervision and time */
/**
 * RCoA supervision distance → the FICM capability scale.
 *
 * Neither logbook trainees import from records the FICM scale; both record how
 * far away the supervisor was. The mapping is decided in
 * `docs/IMPORT_SOURCES.md` and stated to the user before anything is written,
 * because it is an interpretation of their record rather than a reading of it.
 *
 * **Working unsupervised and supervising someone else are both level 4.**
 *
 * Changed on 2026-08-18 at the human's direction, having seen his own logbook
 * imported. The earlier reading put solo work at 3 and reserved 4 for
 * supervising, on the grounds that FICM level 4 is "independent
 * consultant-level practice". His judgement is that a trainee doing the case
 * alone *is* practising independently, which is what the level describes, and
 * that is a clinician's call to make about a clinical scale.
 *
 * `Distant` stays at 3: a supervisor who is reachable is not the same as none
 * at all, and that distinction is the only thing left separating 3 from 4.
 */
const SUPERVISION_LEVELS = {
    observed: 1,
    immediate: 1,
    supervised: 2,
    local: 2,
    // An older RCoA scheme, and the single commonest value in the sample export.
    '2b': 2,
    distant: 3,
    solo: 4,
    independent: 4,
    // In a dropdown whose other options are all distances, "none" means no
    // supervision present. It does not mean "not recorded" — a blank cell is
    // that, and falls back to the wizard's default.
    none: 4,
    supervising: 4,
    teaching: 4,
    leading: 4,
};
export function parseSupervision(value) {
    const text = normalise(value);
    if (text === '')
        return null;
    return SUPERVISION_LEVELS[text] ?? null;
}
/**
 * The words either logbook uses where this app wants a clock time.
 *
 * Neither records one. Both record a band, and day/night and the out-of-hours
 * percentage are derived from a clock time — a figure that reaches the PDF and
 * that a panel reads — so a representative time is chosen and the wizard says
 * which before importing.
 *
 * `evening` is the awkward one. The band 18:00–22:00 straddles this app's 20:00
 * night boundary, so its midpoint lands exactly on it and counts as night.
 * That is the honest reading of evening work as out-of-hours, but it is a
 * choice rather than a fact, which is why the wizard shows it.
 */
const TIME_BANDS = {
    'morning 0800 1300': { hours: 10, minutes: 30 },
    morning: { hours: 10, minutes: 30 },
    'afternoon 1300 1800': { hours: 15, minutes: 30 },
    afternoon: { hours: 15, minutes: 30 },
    'evening 1800 2200': { hours: 20, minutes: 0 },
    evening: { hours: 20, minutes: 0 },
    'night 2200 0800': { hours: 3, minutes: 0 },
    night: { hours: 3, minutes: 0 },
    day: { hours: 12, minutes: 0 },
};
/** `night-2200-0800` or `Night` → `03:00`. Null if it is not a band at all. */
export function parseTimeBand(value) {
    const text = normalise(value);
    if (text === '')
        return null;
    return TIME_BANDS[text] ?? null;
}
/**
 * The time a row with no usable time at all is given.
 *
 * Midday: a weekday daytime entry, which is the least-claiming option
 * available. Anything in the small hours would inflate the out-of-hours
 * percentage on the strength of a missing cell.
 */
export const DEFAULT_IMPORT_HOUR = 12;
/** One value, matched against the catalogue. Null where nothing fits. */
export function procedureKeyFor(value) {
    return matchProcedureValues([value])[0]?.procedureKey ?? null;
}
/**
 * Matches the procedure names in someone else's logbook against the catalogue,
 * using the same labels and synonyms the picker searches.
 *
 * `confident` marks an exact hit on a label or synonym. Anything less is still
 * offered — a wrong suggestion the user can see and change is more useful than
 * no suggestion — but the wizard shows the two groups separately so nothing
 * uncertain is accepted by inattention.
 */
export function matchProcedureValues(values) {
    return [...new Set(values.map((value) => value.trim()).filter((value) => value !== ''))]
        .sort((a, b) => a.localeCompare(b, 'en-GB'))
        .map((value) => {
        const needle = normalise(value);
        for (const procedure of PROCEDURES) {
            if (normalise(procedure.label) === needle) {
                return { value, procedureKey: procedure.key, confident: true };
            }
            for (const synonym of procedure.synonyms ?? []) {
                if (normalise(synonym) === needle) {
                    return { value, procedureKey: procedure.key, confident: true };
                }
            }
        }
        /*
         * Then the other direction: someone else's "Arterial line insertion
         * (radial)" contains our label, even though our search — which is built
         * for someone typing three letters into a picker — would not find it from
         * a query that long. Longest label first, so "Chest drain (Seldinger)"
         * wins over any shorter label it happens to contain.
         */
        const contained = [...PROCEDURES]
            .flatMap((procedure) => [procedure.label, ...(procedure.synonyms ?? [])].map((term) => ({ procedure, term })))
            .filter(({ term }) => term.length > 3 && needle.includes(normalise(term)))
            .sort((a, b) => b.term.length - a.term.length)[0];
        if (contained !== undefined) {
            return { value, procedureKey: contained.procedure.key, confident: false };
        }
        const [suggestion] = searchProcedures(value);
        return { value, procedureKey: suggestion?.key ?? null, confident: false };
    });
}
/** "3", "Level 3", "3 — performs in most circumstances", "III". */
export function parseCapabilityLevel(value) {
    const text = normalise(value);
    if (text === '')
        return null;
    const roman = { i: 1, ii: 2, iii: 3, iv: 4 };
    const romanMatch = roman[text];
    if (romanMatch !== undefined)
        return romanMatch;
    const digit = /(\d)/.exec(text);
    if (digit === null)
        return null;
    const level = Number(digit[1]);
    return CAPABILITY_LEVELS.includes(level) ? level : null;
}
/** Someone else's age column: a band, a number, or a phrase. */
export function parseAgeBand(value) {
    const text = normalise(value);
    if (text === '')
        return null;
    const direct = AGE_BANDS.find((band) => normalise(band) === text);
    if (direct !== undefined)
        return direct;
    if (text.includes('under 16') || text.includes('paediatric') || text.includes('child')) {
        return 'under_16';
    }
    if (text.includes('over 85'))
        return 'over_85';
    const number = /(\d{1,3})/.exec(text);
    if (number === null)
        return null;
    const age = Number(number[1]);
    if (age < 16)
        return 'under_16';
    if (age <= 30)
        return '16_30';
    if (age <= 50)
        return '31_50';
    if (age <= 70)
        return '51_70';
    if (age <= 85)
        return '71_85';
    return 'over_85';
}
/**
 * The Excel ICU logbook's `Level of care` column → a `CareLevel`.
 *
 * **The exact strings this column holds have not been seen.** The real
 * workbooks live on the human's own machine and the tests that read them skip
 * everywhere else (see PROGRESS.md), so this handles the forms such a column
 * plausibly takes — a bare digit, "Level 2", "L3", and the ward/HDU/ICU words
 * British units use for the same thing — and returns null for anything else.
 *
 * Returning null matters: the caller keeps an unparsed value in the note
 * rather than dropping it. If this guesses wrong the trainee loses nothing,
 * which is the only acceptable failure mode for an importer.
 */
export function parseCareLevel(value) {
    const text = normalise(value);
    if (text === '')
        return null;
    // "level 3", "l3", "3", "3 (icu)" — the digit is the signal when present.
    const digit = /(?:^|[^\d])([0-3])(?:[^\d]|$)/.exec(text);
    if (digit !== null)
        return Number(digit[1]);
    if (text.includes('icu') || text.includes('itu') || text.includes('intensive'))
        return 3;
    if (text.includes('hdu') || text.includes('high dependency'))
        return 2;
    if (text.includes('ward'))
        return 0;
    return null;
}
export function parseBoolean(value) {
    const text = normalise(value);
    if (text === '')
        return undefined;
    if (['yes', 'y', 'true', '1', 'successful', 'success', 'done'].includes(text))
        return true;
    if (['no', 'n', 'false', '0', 'unsuccessful', 'failed', 'failure'].includes(text))
        return false;
    return undefined;
}
function cell(row, header) {
    return header === undefined ? '' : (row[header] ?? '').trim();
}
/**
 * Turns parsed CSV rows into entries, reporting every row it cannot take and
 * why. Nothing is dropped silently: a trainee importing 400 rows must be able
 * to see the 6 that did not come across and decide what to do about them.
 */
export function planImport(rows, settings) {
    const imported = [];
    const skipped = [];
    rows.forEach((row, index) => {
        const rawProcedure = cell(row, settings.mapping.procedure);
        if (rawProcedure === '') {
            skipped.push({ index, reason: 'No procedure named', value: '' });
            return;
        }
        const mapped = settings.procedureMap[rawProcedure];
        if (mapped === null) {
            skipped.push({ index, reason: 'Procedure skipped by you', value: rawProcedure });
            return;
        }
        if (mapped === undefined || procedureByKey(mapped) === undefined) {
            skipped.push({ index, reason: 'Procedure not matched', value: rawProcedure });
            return;
        }
        const rawDate = cell(row, settings.mapping.date);
        const occurredAt = parseImportedDate(rawDate, settings.dateFormat, cell(row, settings.mapping.time));
        if (occurredAt === null) {
            skipped.push({ index, reason: 'Date could not be read', value: rawDate });
            return;
        }
        const definition = procedureByKey(mapped);
        const variant = cell(row, settings.mapping.variant);
        const matchedVariant = (definition?.variants ?? []).find((option) => normalise(option) === normalise(variant));
        const side = normalise(cell(row, settings.mapping.side));
        const sideValue = side.startsWith('l') && definition?.sideApplicable === true
            ? 'left'
            : side.startsWith('r') && definition?.sideApplicable === true
                ? 'right'
                : side.startsWith('b') && definition?.sideApplicable === true
                    ? 'bilateral'
                    : undefined;
        const ultrasound = definition?.usGuidanceApplicable === true
            ? parseBoolean(cell(row, settings.mapping.ultrasoundGuided))
            : undefined;
        const attempts = Number(cell(row, settings.mapping.attempts));
        const notes = cell(row, settings.mapping.note);
        imported.push({
            index,
            procedureKey: mapped,
            occurredAt,
            ageBand: parseAgeBand(cell(row, settings.mapping.ageBand)) ?? settings.defaultAgeBand,
            capabilityLevel: parseCapabilityLevel(cell(row, settings.mapping.capabilityLevel)) ??
                settings.defaultCapabilityLevel,
            ...(matchedVariant === undefined ? {} : { variant: matchedVariant }),
            ...(sideValue === undefined ? {} : { side: sideValue }),
            ...(ultrasound === undefined ? {} : { ultrasoundGuided: ultrasound }),
            ...(Number.isInteger(attempts) && attempts >= 1 && attempts <= 10 ? { attempts } : {}),
            ...(() => {
                const successful = parseBoolean(cell(row, settings.mapping.successful));
                return successful === undefined ? {} : { successful };
            })(),
            ...(cell(row, settings.mapping.complications) === ''
                ? {}
                : { complications: cell(row, settings.mapping.complications).slice(0, 300) }),
            ...(cell(row, settings.mapping.indication) === ''
                ? {}
                : { indication: cell(row, settings.mapping.indication).slice(0, 200) }),
            ...(notes === '' ? {} : { note: notes.slice(0, 500) }),
            sourceProcedure: rawProcedure,
        });
    });
    return { rows: imported, skipped };
}
/**
 * The deduplication key: date, time, kind and procedure.
 *
 * Two arterial lines on the same patient an hour apart are two entries; the
 * same row imported twice is one. This is what makes re-importing a spreadsheet
 * after adding a few lines to it safe.
 */
export function dedupeKey(input) {
    const date = input.occurredAt;
    const stamp = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
        String(date.getHours()).padStart(2, '0'),
        String(date.getMinutes()).padStart(2, '0'),
    ].join('');
    return `${input.kind ?? 'procedure'}|${stamp}|${input.procedureKey}`;
}
/** The template a trainee can download and fill in. */
export function importTemplateCsv() {
    const headers = [
        'Date',
        'Time',
        'Procedure',
        'Variant or site',
        'Side',
        'Ultrasound guided',
        'Capability level',
        'Age band',
        'Attempts',
        'Successful',
        'Complications',
        'Indication',
        'Note',
    ];
    const example = [
        '14/03/2026',
        '21:20',
        'Arterial line',
        'Radial',
        'Left',
        'Yes',
        '3',
        '71-85',
        '1',
        'Yes',
        '',
        'Vasopressor support',
        '',
    ];
    return `${headers.join(',')}\r\n${example.join(',')}\r\n`;
}
//# sourceMappingURL=import.js.map